import { DepositEntry } from '../../../types';
import { useSubmitDeposit } from '../hooks/useSubmitDeposit';
import { formatNumber } from '../../../../../helpers/NumberFormatter';
import '../../ManualDistribution/ConfirmationModal/index.css';

interface Props {
  changedEntries: DepositEntry[];
  onClose: () => void;
}

const DepositConfirmationModal = ({ changedEntries, onClose }: Props) => {
  const { submit, state, progress, error, resetState } = useSubmitDeposit();

  const totalByQuoteAsset = changedEntries.reduce<Record<string, number>>(
    (acc, e) => {
      acc[e.quoteAsset] = (acc[e.quoteAsset] ?? 0) + e.depositAmount;
      return acc;
    },
    {},
  );

  const handleConfirm = () => {
    submit(changedEntries);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <div className="confirmation-overlay" onClick={handleClose}>
      <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="confirmation-title">Confirm Deposit Distribution</h3>

        {state === 'idle' && (
          <>
            <p className="deposit-confirm-summary">
              {Object.entries(totalByQuoteAsset).map(([qa, total]) => (
                <span key={qa}>
                  Depositing{' '}
                  <strong>
                    {formatNumber(total, 2)} {qa}
                  </strong>{' '}
                </span>
              ))}
              across {changedEntries.length} subscription
              {changedEntries.length !== 1 ? 's' : ''}
            </p>
            <ul className="confirmation-list">
              {changedEntries.map((entry) => (
                <li key={entry.subscriptionId} className="confirmation-item">
                  <span className="confirmation-item-name">
                    {entry.botFriendlyName}
                  </span>
                  <span className="confirmation-item-change">
                    <span className="confirmation-item-detail">
                      +{formatNumber(entry.depositAmount, 2)} {entry.quoteAsset}
                    </span>
                    <span className="confirmation-item-detail">
                      {formatNumber(entry.currentStableBalance, 2)} →{' '}
                      {formatNumber(
                        entry.currentStableBalance + entry.depositAmount,
                        2,
                      )}{' '}
                      {entry.quoteAsset}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="confirmation-actions">
              <button className="confirmation-btn cancel" onClick={handleClose}>
                Cancel
              </button>
              <button
                className="confirmation-btn confirm"
                onClick={handleConfirm}
              >
                Confirm
              </button>
            </div>
          </>
        )}

        {state === 'submitting' && progress && (
          <>
            <p className="confirmation-progress">
              Depositing into {progress.currentSub}... Step {progress.current}{' '}
              of {progress.total}
            </p>
            <div className="confirmation-progress-bar">
              <div
                className="confirmation-progress-fill"
                style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                }}
              />
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <p className="confirmation-error">{error}</p>
            <div className="confirmation-actions">
              <button className="confirmation-btn cancel" onClick={handleClose}>
                Close
              </button>
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <p className="confirmation-success">
              Deposit completed successfully.
            </p>
            <div className="confirmation-actions">
              <button
                className="confirmation-btn confirm"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DepositConfirmationModal;
